import PropTypes from 'prop-types'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { I18n, Translate } from 'react-redux-i18n'
import { UseTheme } from '../../hooks/useTheme'
import { UseTypeFontSize } from '../../hooks/useTypeFontSize'
import { TextEllipsisStaticLine } from '../../textEllipsis/TextEllipsisStaticLine'
import { Style } from '../Style'
import { Element } from '../element'
import { getStyle } from '../helper'
import { Icon } from '../icon'
import classes from './index.module.scss'

export const Input = ({
  value,
  containerStyle,
  inputRef,
  placeHolder,
  textInvalid,
  style,
  type,
  onClick,
  onBlur,
  onChange,
  onKeyDown,
  onClickIcon,
  isShowLabel,
  isShowIcon,
  isValid,
  alwaysActiveBorder,
  styleLabel,
  classNameInput,
  defaultValue,
  disabled,
  customElement,
  classIcon,
  styleIcon,
  noControl,
  styleTextInvalid,
  textInvalidEllipsis,
  labelTextEllipsis,
  isShowIconSelect,
  ...others
}) => {
  const typeFontSize = UseTypeFontSize()
  const theme = UseTheme()
  const locale = useSelector((state) => state.i18n.locale)

  const [text, setText] = useState(defaultValue)
  const [isFocus, setIsFocus] = useState(false)

  const getPlaceHolder = useMemo(() => {
    return isShowLabel && isFocus ? '' : I18n.t(placeHolder)
  }, [locale, placeHolder, isShowLabel, isFocus])

  const ref = useRef(null)

  const onHandleClick = (event) => {
    onClick(event)
    setIsFocus(true)
  }

  const onHandleBlur = (event) => {
    onBlur(event)
    setIsFocus(false)
  }

  const onHandleChange = (event) => {
    if (value === undefined) {
      setText(event.target.value)
    }
    onChange && onChange(event)
  }

  const onHandleClickIcon = () => {
    if (text.trim().length > 0) {
      onClickIcon(text)
    }
  }

  useEffect(() => {
    if (value !== undefined) {
      setText(value)
    } else if (disabled) {
      setText(' ')
    }
  }, [value])

  return (
    <div ref={inputRef} className={classes.container} style={containerStyle}>
      <Style
        className={classes.input}
        pseudo="placeholder"
        attr="color"
        color="#9c9c9c"
      >
        <input
          ref={ref}
          disabled={disabled}
          value={noControl ? value : text}
          className={[
            classes.input,
            text ? classes.textExist : '',
            !isShowIcon && !isShowIconSelect ? classes.noIcon : '',
            classNameInput,
          ].join(' ')}
          placeholder={getPlaceHolder}
          onClick={onHandleClick}
          onBlur={onHandleBlur}
          onKeyDown={onKeyDown}
          onChange={onHandleChange}
          style={getStyle(
            {
              fontSize: 14,
              borderColor:
                alwaysActiveBorder || isFocus ? '#2C73E8' : '#E0E0E0',
              backgroundColor: '#FFFFFF',
              color: disabled ? '#9c9c9c' : '#3A414B',
              ...style,
            },
            theme,
            typeFontSize,
          )}
          type={type}
          {...others}
        />
      </Style>

      {isShowLabel && (isFocus || text) && placeHolder && (
        <Element
          elementName="span"
          style={{
            backgroundColor: '#F3F3F3',
            padding: '2px 4px',
            left: 6,
            top: -14,
            color: !isValid ? '#E0505B' : '#3A414B',
            ...styleLabel,
          }}
          className="fw600 positionAbsolute"
        >
          {labelTextEllipsis ? (
            <TextEllipsisStaticLine
              text={placeHolder}
              zIndexTooltip={9999}
              lineClamp={1}
            />
          ) : (
            <Translate value={placeHolder} />
          )}
        </Element>
      )}
      {!isValid && (
        <Element
          elementName="span"
          style={{
            left: 0,
            bottom: -16,
            color: '#E0505B',
            ...styleTextInvalid,
          }}
          className="positionAbsolute"
        >
          {textInvalidEllipsis ? (
            <TextEllipsisStaticLine text={textInvalid} zIndexTooltip={9999} />
          ) : (
            <Translate value={textInvalid} />
          )}
        </Element>
      )}
      {isShowIcon && (
        <Icon
          className={[classIcon, text ? classes.textExist : ''].join(' ')}
          style={{ color: text ? '#2C73E8' : '#9C9C9C', ...styleIcon }}
          onClick={onHandleClickIcon}
        />
      )}

      {isShowIconSelect && !isShowIcon && (
        <Icon
          onClick={onClickIcon}
          className={[classes.textExist, 'icon-caret-bottom'].join(' ')}
          style={{
            fontSize: 8,
            ...styleIcon,
          }}
        />
      )}

      {customElement}
    </div>
  )
}

Input.propTypes = {
  ref: PropTypes.object,
  value: PropTypes.string,
  placeHolder: PropTypes.string,
  textInvalid: PropTypes.string,
  style: PropTypes.object,
  type: PropTypes.string,
  onClick: PropTypes.func,
  onBlur: PropTypes.func,
  onKeyDown: PropTypes.func,
  onChange: PropTypes.func,
  isShowLabel: PropTypes.bool,
  isShowIcon: PropTypes.bool,
  isValid: PropTypes.bool,
  alwaysActiveBorder: PropTypes.bool,
  styleLabel: PropTypes.object,
  defaultValue: PropTypes.string,
  customElement: PropTypes.any,
  classIcon: PropTypes.string,
  styleIcon: PropTypes.object,
  noControl: PropTypes.bool,
  styleTextInvalid: PropTypes.object,
  onClickIcon: PropTypes.func,
}

Input.defaultProps = {
  placeHolder: '',
  textInvalid: '',
  style: {},
  type: 'text',
  onClick: () => {},
  onBlur: () => {},
  onChange: () => {},
  onKeyDown: () => {},
  isShowLabel: false,
  isShowIcon: true,
  isValid: true,
  alwaysActiveBorder: false,
  styleLabel: {},
  defaultValue: '',
  customElement: null,
  classIcon: 'icon-search',
  styleIcon: {},
  noControl: false,
  styleTextInvalid: {},
  onClickIcon: () => {},
}
